import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SquarePaymentCard from '../components/SquarePaymentCard';
import ErrorModal from '../components/ErrorModal';
import { prysmDB } from '../utils/database';

/**
 * Payment Page Component
 * 
 * This page integrates the Square payment processing system into the registration flow.
 * It handles payment completion, error management, and navigation to the next step.
 * 
 * @component
 * @returns {JSX.Element} Payment page component
 */

const Payment = () => {
    // ================================
    // STATE MANAGEMENT
    // ================================

    const [paymentAmount, setPaymentAmount] = useState(0);
    const [userData, setUserData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentAlreadyCompleted, setPaymentAlreadyCompleted] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    const navigate = useNavigate();

    // ================================
    // EFFECTS
    // ================================

    /**
     * Loads user data and payment amount on component mount
     */
    useEffect(() => {
        loadUserData();
    }, []);

    // ================================
    // DATA LOADING
    // ================================

    /**
     * Loads user registration data and determines payment amount
     */
    const loadUserData = async () => {
        try {
            console.log('[Payment] Loading user data for payment processing...');

            // Check for existing successful payment
            const existingPayment = await prysmDB.get('payment_result', 'current_payment');
            if (existingPayment && (existingPayment.success || existingPayment.paymentStatus === 'completed')) {
                console.log('[Payment] Payment already completed:', existingPayment);
                setPaymentAlreadyCompleted(true);
                setIsLoading(false);
                return;
            }

            // Load registration data from storage
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (registrationData) {
                setUserData(registrationData);
                console.log('[Payment] Registration data loaded from storage:', {
                    hasFirstName: !!registrationData.first_name,
                    hasEmail: !!registrationData.email,
                    hasData: Object.keys(registrationData).length > 0
                });

                // Also check registration data for payment completion (backward compatibility)
                if (registrationData.paymentStatus === 'completed' || registrationData.paymentCompleted) {
                    console.log('[Payment] Payment already completed in registration data:', registrationData);
                    setPaymentAlreadyCompleted(true);
                    setIsLoading(false);
                    return;
                }
            }

            // Determine payment amount (example: $25.00 registration fee)
            // In a real application, this would come from your pricing logic
            const amount = 2500; // $25.00 in cents
            setPaymentAmount(amount);

            console.log('[Payment] Payment amount set:', amount);

        } catch (error) {
            console.error('[Payment] Error loading user data:', error);
            setError({
                type: 'data_loading',
                message: 'Failed to load user data',
                details: error
            });
        } finally {
            setIsLoading(false);
        }
    };

    // ================================
    // EVENT HANDLERS
    // ================================

    /**
     * Handles successful payment completion
     * @param {Object} result - Payment result from Square
     */
    const handlePaymentSuccess = async (result) => {
        try {
            console.log('[Payment] Payment successful:', result);

            // Store payment information in localStorage
            const paymentData = {
                success: true,
                paymentId: result.paymentId || result.transactionId,
                amount: paymentAmount,
                timestamp: new Date().toISOString(),
                squareData: result
            };

            // Note: Payment data is already stored in registration_data by SquarePaymentCard component

            // Update registration data with payment status
            const registrationData = await prysmDB.get('registration_data', 'current_registration');
            if (registrationData) {
                registrationData.paymentCompleted = true;
                registrationData.paymentId = paymentData.paymentId;
                registrationData.paymentTimestamp = paymentData.timestamp;
                await prysmDB.set('registration_data', 'current_registration', registrationData);

                // Dispatch event to notify other components of data update
                window.dispatchEvent(new CustomEvent('registrationDataUpdated', { detail: registrationData }));
            }

            // Navigate to thank you page
            console.log('[Payment] Navigating to thank you page...');
            navigate('/thank-you');

        } catch (error) {
            console.error('[Payment] Error handling payment success:', error);
            setError({
                type: 'payment_success_handling',
                message: 'Payment completed but encountered an error',
                details: error
            });
        }
    };

    /**
     * Handles payment errors
     * @param {Object} error - Payment error details
     */
    const handlePaymentError = (error) => {
        console.error('[Payment] Payment error:', error);

        setError({
            type: 'payment_processing',
            message: error.message || 'Payment processing failed',
            details: error
        });
        setShowErrorModal(true);
    };

    /**
     * Handles navigation back to previous step
     */
    const handleGoBack = () => {
        console.log('[Payment] Navigating back to checkout...');
        navigate('/checkout');
    };

    /**
     * Handles retry payment
     */
    const handleRetry = () => {
        setError(null);
        setShowErrorModal(false);
        console.log('[Payment] Retrying payment...');
    };

    /**
     * Handles navigation when payment is already completed
     */
    const handlePaymentAlreadyCompleted = () => {
        console.log('[Payment] Payment already completed, navigating to next step...');
        navigate('/upload-selfie');
    };

    // ================================
    // RENDER HELPERS
    // ================================

    /**
     * Renders error state
     * @returns {JSX.Element} Error display
     */
    const renderError = () => {
        if (!error) return null;

        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>

                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Payment Error
                            </h1>

                            <p className="text-gray-600 mb-6">
                                {error.message}
                            </p>

                            <div className="space-y-4">
                                <button
                                    onClick={handleRetry}
                                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                                >
                                    Try Again
                                </button>

                                <button
                                    onClick={handleGoBack}
                                    className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-300"
                                >
                                    Go Back
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Renders loading state
     * @returns {JSX.Element} Loading display
     */
    const renderLoading = () => {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-white text-lg">Loading payment system...</p>
                </div>
            </div>
        );
    };

    /**
     * Renders payment already completed state
     * @returns {JSX.Element} Payment completed display
     */
    const renderPaymentAlreadyCompleted = () => {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>

                            <h1 className="text-3xl font-bold text-gray-900 mb-4">
                                Payment Already Completed
                            </h1>

                            <p className="text-gray-600 mb-8 text-lg">
                                Your payment has already been successfully processed. You can now continue with the next step in your registration.
                            </p>

                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                                <div className="flex items-center">
                                    <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-green-800 font-medium">Payment Status: Completed</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <button
                                    onClick={handlePaymentAlreadyCompleted}
                                    className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-lg font-medium hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                                >
                                    Continue to Next Step
                                </button>

                                <button
                                    onClick={handleGoBack}
                                    className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-300"
                                >
                                    Back to Checkout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ================================
    // MAIN RENDER
    // ================================

    if (isLoading) {
        return renderLoading();
    }

    if (paymentAlreadyCompleted) {
        return renderPaymentAlreadyCompleted();
    }

    if (error && !showErrorModal) {
        return renderError();
    }

    return (
        <div>
            <SquarePaymentCard
                amount={paymentAmount}
                currency="USD"
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                userData={userData}
            />
            
            {/* Error Modal for Payment Page Level Errors */}
            <ErrorModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                error={error}
                title="Payment Error"
                showRetry={true}
                onRetry={handleRetry}
            />
        </div>
    );
};

export default Payment;
